import React from 'react';
import { Navigate } from 'react-router';
import { useBillingIa } from '../../hooks/useBillingIa';
import { useAuth } from '../../hooks/useAuth';

/**
 * Route helpers for the External-portal IA switch (plan P12/P15). Everything here is a no-op unless
 * the signed-in user is External AND `billingEnabled` — the legacy routes render exactly as before.
 */

/**
 * Legacy path kept alive (plan P12: "old routes redirect, nothing is deleted"): under the new IA it
 * redirects to its replacement; under the dark flag it renders the legacy page unchanged — so the
 * legacy sidebar item, its selection highlight and AppBar title stay byte-identical while dark.
 */
export const BillingIaRedirect: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => {
  const billingIa = useBillingIa();
  return billingIa ? <Navigate to={to} replace /> : <>{children}</>;
};

/**
 * Render one element under the new IA and another while dark (plan P12/P15). `/portal/samples` shows
 * the Phase 4b `SamplesPage` vs the legacy `MySubmissionsPage`; `/portal/reports` shows the Phase 4b
 * `ReportsPage` (which renders its OWN empty state for a free-tier account — never a redirect,
 * plan P11) vs the legacy `AgentPortalDashboard`.
 */
export const BillingIaSwitch: React.FC<{ ia: React.ReactNode; dark: React.ReactNode }> = ({ ia, dark }) => {
  const billingIa = useBillingIa();
  return <>{billingIa ? ia : dark}</>;
};

/**
 * Gate the External Smart Sampling routes (SS External self-service — Phase 4a). The whole surface is
 * dark unless the account is a boundary-only External on the new IA (useBillingIa) AND the global
 * `smartSamplingExternalEnabled` flag is on — mirroring the menu + Home-card gating. While dark, a
 * direct navigation is bounced to Home rather than rendering a page whose façade calls all 404.
 */
export const SmartSamplingExternalRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const billingIa = useBillingIa();
  const { user } = useAuth();
  const enabled = billingIa && user?.smartSamplingExternalEnabled === true;
  return enabled ? <>{children}</> : <Navigate to="/portal/home" replace />;
};
